/**
 * Leadership Progress Details Page
 *
 * Leadership version of Progress Details with slicers, heatmap, and drill-down.
 * Shows TeamSummaryCards + TeamMeasureChart + ProviderHeatmap (with inline clinician expansion).
 *
 * Phase 7: Replaced page-navigation to EnhancedProviderView with inline expansion
 * inside ProviderHeatmap. The team view is always visible.
 */

import { useEffect, useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { BarChart3, DollarSign, Users } from 'lucide-react'
import { LeaderProfileBar } from './components/LeaderProfileBar'
import { ADCOEMeasureDrilldown } from './components/ADCOEMeasureDrilldown'
import { PatientModal } from '@/views/equip/modals/PatientModal'
import { AuditModal } from '@/views/equip/modals/AuditModal'
import { equipApi } from '@/services/equipApi'
import { ProviderHeatmap } from './components/ProviderHeatmap'
import { TeamMeasureChart } from './components/TeamMeasureChart'
import { IncentiveProjectionCard } from './components/IncentiveProjectionCard'
import { useCurrentQuarter } from '@/hooks/equip/useCurrentQuarter'
import { useEquipScopeOptional } from '@/views/equip/context/EquipScopeContext'
import { useAvailableQuarters } from '@/hooks/equip/useEquipDashboard'
import { useLeadershipAggregation } from '@/hooks/equip/useEquipDashboard'
import { useMyContext } from '@/hooks/equip/useEquipPerformance'
import type {
  LeadershipAggregation,
} from '@/lib/equip-types'

interface LeadershipProgressDetailsProps {
  managerEmail?: string | null
  nested?: boolean         // when true: hides header, quarter selector, dollar toggle
  year?: number            // used when nested=true to sync with parent's quarter
  quarter?: number
  canSeeAwards?: boolean   // when nested=true, parent controls dollar visibility
}

export function LeadershipProgressDetails({
  managerEmail,
  nested = false,
  year: yearProp,
  quarter: quarterProp,
  canSeeAwards: canSeeAwardsProp,
}: LeadershipProgressDetailsProps = {}) {
  // Current quarter from backend API (cached, gated by EquipScopeContext loading)
  const currentQuarter = useCurrentQuarter()

  // State — keep selectedQuarter (object format), filter controls
  const [selectedQuarter, setSelectedQuarter] = useState({
    year: yearProp ?? currentQuarter.year,
    quarter: quarterProp ?? currentQuarter.quarter,
  })

  // Sync parent quarter changes into local state when nested
  useEffect(() => {
    if (nested && yearProp !== undefined && quarterProp !== undefined) {
      setSelectedQuarter({ year: yearProp, quarter: quarterProp })
    }
  }, [nested, yearProp, quarterProp])

  // Sync quarter to shared context (for snapshot banner) — skip when nested
  // to avoid child mounting overwriting the outer page's selected quarter.
  const scope = useEquipScopeOptional()
  useEffect(() => {
    if (!nested) scope?.setQuarter(selectedQuarter.year, selectedQuarter.quarter)
  }, [nested, selectedQuarter.year, selectedQuarter.quarter]) // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedProviderType, setSelectedProviderType] = useState<string>('all')
  const [selectedCareTeam, setSelectedCareTeam] = useState<string>('all')
  const [sortColumn, setSortColumn] = useState<string>('providerName')
  const [eligibleOnly, setEligibleOnly] = useState(false)
  const [activeOnly, setActiveOnly] = useState(true)
  const [showDollarValues, setShowDollarValues] = useState(true)
  const [selectedMeasure, setSelectedMeasure] = useState<{ name: string; category: string } | null>(null)
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null)
  const [selectedAuditId, setSelectedAuditId] = useState<number | null>(null)
  const [selectedAuditType, setSelectedAuditType] = useState<string>('coding')
  const [exportLoading, setExportLoading] = useState(false)

  // Fetch available quarters for the selector
  const availableQuartersQuery = useAvailableQuarters()
  const quarterStrings = useMemo(() => {
    const currentLabel = `${currentQuarter.year}-Q${currentQuarter.quarter}`
    const snapshotQuarters = (availableQuartersQuery.data ?? []).map(
      (q) => `${q.year}-Q${q.quarter}`
    )
    return [currentLabel, ...snapshotQuarters.filter((q) => q !== currentLabel)]
  }, [currentQuarter.year, currentQuarter.quarter, availableQuartersQuery.data])

  // Determine if the selected quarter is the current quarter
  const isCurrentQuarter =
    selectedQuarter.year === currentQuarter.year &&
    selectedQuarter.quarter === currentQuarter.quarter

  // User context — used for award visibility gating
  const contextQuery = useMyContext()
  const backendCanSeeAwards = contextQuery.data?.canSeeAwards ?? true
  // Effective visibility: in nested mode, parent controls dollar visibility;
  // otherwise backend permission AND local toggle (for screen sharing)
  const canSeeAwards = nested
    ? (canSeeAwardsProp ?? false)
    : backendCanSeeAwards && showDollarValues

  // React Query hook — passes managerEmail prop for scoped data
  const aggQuery = useLeadershipAggregation(
    selectedQuarter.year,
    selectedQuarter.quarter,
    managerEmail ?? undefined
  )

  // Derive data/loading/error to match existing JSX variable names
  const aggregation = aggQuery.data ?? null
  const isLoading = aggQuery.isPending
  const error = aggQuery.error?.message ?? null

  // Handle quarter change from slicer
  const handleQuarterChange = (quarterStr: string) => {
    if (quarterStr === 'all') {
      // Default to current quarter
      setSelectedQuarter({ year: currentQuarter.year, quarter: currentQuarter.quarter })
      return
    }
    const [year, quarterPart] = quarterStr.split('-Q')
    setSelectedQuarter({
      year: parseInt(year, 10),
      quarter: parseInt(quarterPart, 10),
    })
  }

  // Apply filters to aggregation
  const filteredProviders = useMemo(() => {
    if (!aggregation) return []

    let filtered = aggregation.providers

    if (selectedProviderType !== 'all') {
      filtered = filtered.filter((p) => p.bonusEligibleType === selectedProviderType)
    }

    if (selectedCareTeam !== 'all') {
      filtered = filtered.filter((p) => p.careTeam === selectedCareTeam)
    }

    if (eligibleOnly) {
      // Both flags must be true for a clinician to be considered eligible (#86)
      filtered = filtered.filter((p) => p.eligibleAtQuarterStart && p.eligibleAtQuarterEnd)
    }

    if (activeOnly) {
      filtered = filtered.filter((p) => !p.isInactiveInQuarter)
    }

    return filtered
  }, [aggregation, selectedProviderType, selectedCareTeam, eligibleOnly, activeOnly])

  // Recalculate summary metrics from filtered providers.
  // Terminated-in-quarter providers are ALWAYS excluded from rollup values
  // regardless of the activeOnly toggle — they are never meaningful for aggregation.
  const filteredAggregation: LeadershipAggregation | null = useMemo(() => {
    if (!aggregation) return null
    // When a scoped manager's identity can't be resolved, the backend sets
    // managerIdentityUnresolved AND returns zero providers. Preserve the
    // aggregation (with its flag) so LeaderProfileBar renders the "Leader not
    // identified" warning instead of falling through to the generic no-data
    // card — otherwise the true-positive warning is unreachable (audit #1).
    if (filteredProviders.length === 0) {
      return aggregation.managerIdentityUnresolved ? aggregation : null
    }

    // Rollup excludes terminated providers; display count reflects what is shown
    const rollupProviders = filteredProviders.filter((p) => !p.isInactiveInQuarter)

    // Backend returns 0-1 scale; compare against 0.89
    const atRiskCount = rollupProviders.filter(
      (p) => p.totalPercentage !== null && (p.totalPercentage * 100) < 89
    ).length

    const avgTotalPercentage =
      rollupProviders.length > 0
        ? rollupProviders.reduce((sum, p) => sum + (p.totalPercentage ?? 0), 0) /
          rollupProviders.length
        : 0

    const totalProjectedAwards = rollupProviders.reduce(
      (sum, p) => sum + (p.calculatedAward ?? 0),
      0
    )

    const totalPatients = rollupProviders.reduce(
      (sum, p) => sum + (p.totalPatientCount ?? 0),
      0
    )

    const totalAcoPatients = rollupProviders.reduce(
      (sum, p) => sum + (p.acoPatientCount ?? 0),
      0
    )

    return {
      ...aggregation,
      providers: filteredProviders,
      totalProviders: filteredProviders.length,
      atRiskCount,
      avgTotalPercentage,
      totalProjectedAwards,
      totalPatients,
      totalAcoPatients,
    }
  }, [aggregation, filteredProviders])

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedProviderType('all')
    setSelectedCareTeam('all')
    setActiveOnly(true)
  }

  const handleMeasureClick = useCallback((measureName: string, measureCategory: string) => {
    setSelectedMeasure((prev) =>
      prev?.name === measureName ? null : { name: measureName, category: measureCategory }
    )
  }, [])

  const handleExportClick = useCallback(async () => {
    if (!aggregation?.incentiveProjection) return
    setExportLoading(true)
    try {
      const blob = await equipApi.export.downloadLeadershipReport(
        aggregation.incentiveProjection.leaderEmail,
        selectedQuarter.year,
        selectedQuarter.quarter
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `EQuIP_Leadership_Report_Q${selectedQuarter.quarter}_${selectedQuarter.year}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExportLoading(false)
    }
  }, [aggregation, selectedQuarter])

  return (
    <div className="space-y-3">
      {/* Page header — hidden when nested inside a parent page */}
      {!nested && (
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <h1 className="text-2xl font-bold">Leadership Progress Details</h1>
            <p className="text-muted-foreground mt-1">
              Team performance breakdown and provider heatmap
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link to="/equip/aco-patients">
                <Users className="h-4 w-4 mr-1" />
                ACO Patients
              </Link>
            </Button>
            {backendCanSeeAwards && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Label htmlFor="show-dollar-values" className="text-sm leading-none text-muted-foreground whitespace-nowrap cursor-pointer">
                  Show dollar values
                </Label>
                <Switch
                  id="show-dollar-values"
                  checked={showDollarValues}
                  onCheckedChange={setShowDollarValues}
                  className="shrink-0"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error state — different treatment for current vs historical quarter */}
      {error && (
        isCurrentQuarter ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold">No Data for Q{selectedQuarter.quarter} {selectedQuarter.year}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Historical data will be available here once the quarter is complete.
                </p>
              </div>
              <Button onClick={() => {
                setSelectedQuarter({ year: currentQuarter.year, quarter: currentQuarter.quarter })
              }}>
                View Current Quarter
              </Button>
            </CardContent>
          </Card>
        )
      )}

      {/* Leader Profile Bar — expandable; IncentiveProjectionCard lives inside when available */}
      <LeaderProfileBar
        aggregation={filteredAggregation}
        isLoading={isLoading}
        isCurrentQuarter={isCurrentQuarter}
        canSeeAwards={canSeeAwards}
        expandedContent={
          aggregation?.incentiveProjection
            ? <IncentiveProjectionCard
                  projection={aggregation.incentiveProjection}
                  quarter={selectedQuarter.quarter}
                  onMeasureClick={handleMeasureClick}
                  activeMeasure={selectedMeasure?.name ?? null}
                  onExportClick={handleExportClick}
                  exportLoading={exportLoading}
                  renderDrilldown={(measureName, measureCategory, measure) => (
                    <ADCOEMeasureDrilldown
                      measureName={measureName}
                      measureCategory={measureCategory}
                      leaderEmail={aggregation.incentiveProjection!.leaderEmail}
                      year={selectedQuarter.year}
                      quarter={selectedQuarter.quarter}
                      measure={measure}
                      onPatientClick={(patientId) => setSelectedPatientId(String(patientId))}
                      onAuditClick={(auditId, auditType) => {
                        setSelectedAuditId(auditId)
                        setSelectedAuditType(auditType)
                      }}
                    />
                  )}
                />
            : undefined
        }
      />

      {/* Team Measure Chart */}
      <TeamMeasureChart
        providers={filteredProviders}
        isLoading={isLoading}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
      />

      {/* Provider Heatmap — filter controls (quarter, type, eligible) live inside the card header */}
      <ProviderHeatmap
        providers={filteredProviders}
        isLoading={isLoading}
        availableQuarters={nested
          ? [`${selectedQuarter.year}-Q${selectedQuarter.quarter}`]
          : quarterStrings}
        sortColumn={sortColumn}
        onSortChange={setSortColumn}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
        selectedQuarter={`${selectedQuarter.year}-Q${selectedQuarter.quarter}`}
        onQuarterChange={nested ? () => {} : handleQuarterChange}
        selectedProviderType={selectedProviderType}
        onProviderTypeChange={setSelectedProviderType}
        selectedCareTeam={selectedCareTeam}
        onCareTeamChange={setSelectedCareTeam}
        eligibleOnly={eligibleOnly}
        onEligibleOnlyChange={setEligibleOnly}
        activeOnly={activeOnly}
        onActiveOnlyChange={setActiveOnly}
        allProviders={aggregation?.providers ?? []}
        onClearFilters={handleClearFilters}
        canSeeAwards={canSeeAwards}
      />

      <PatientModal
        patientId={selectedPatientId}
        onClose={() => setSelectedPatientId(null)}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
      />

      <AuditModal
        auditId={selectedAuditId}
        auditType={selectedAuditType}
        onClose={() => setSelectedAuditId(null)}
        year={selectedQuarter.year}
        quarter={selectedQuarter.quarter}
      />
    </div>
  )
}
